import { configureStore } from "@reduxjs/toolkit";
import {
  persistStore,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import persistReducer from "redux-persist/es/persistReducer";
import rootReducer from "./reducers/rootReducer";
import {
  requestSessionFromOtherTabs,
  writeSnapshotToThisTab,
} from "../services/authSync";

// Storage adapter for redux-persist. The live session stays in this tab's
// sessionStorage (so it ends when the browser closes); a tab that starts
// without one borrows the session of an already-open tab — see
// services/authSync.ts for the handshake and for the live cross-tab sync.
const sharedSessionStorage = {
  getItem: async (key: string): Promise<string | null> => {
    // 1. This tab's own session (reloads, and every tab after its first load).
    const sessionValue = sessionStorage.getItem(key);
    if (sessionValue) return sessionValue;

    // 2. Ask the other open tabs. Resolves null for detached tabs — notably the
    // public Try Haire demo, which must NEVER inherit a real logged-in session:
    // a visitor (or a recruiter showing the demo) would otherwise land inside
    // someone's actual account. It builds its own throwaway one via verifyTryOtp.
    try {
      const snapshot = await requestSessionFromOtherTabs();
      if (!snapshot) return null;
      writeSnapshotToThisTab(snapshot);
      return snapshot.root;
    } catch (e) {
      console.error("Cross-tab session bootstrap failed:", e);
      return null;
    }
  },
  setItem: (key: string, value: string): Promise<void> => {
    sessionStorage.setItem(key, value);
    return Promise.resolve();
  },
  removeItem: (key: string): Promise<void> => {
    sessionStorage.removeItem(key);
    return Promise.resolve();
  },
};

const persistConfig = {
  key: "root",
  storage: sharedSessionStorage,
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          FLUSH,
          REHYDRATE,
          PAUSE,
          PERSIST,
          PURGE,
          REGISTER,
          "company/updateCompanyDetails",
        ],
        ignoredPaths: ["company.details.profile_pic"],
      },
    }),
});

export const persistor = persistStore(store);
